import React, { useEffect, useState } from "react";
import { isLocalCarrierOrder } from "../../helpers/functions/is-local-carrier";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";
import { Link } from "react-router-dom";
import { getGangsheetDownloadLabel } from "../../helpers/functions/gangsheet-link-label";
import { getOrderRowBackgroundColor, getOrderRowBackgroundColorSoft } from "../../helpers/functions/order-row-color";

const getTodayDate = () => new Date().toISOString().split("T")[0];

const CAOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortColumn, setSortColumn] = useState("commitment_date");
  const [notes, setNotes] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [shipped, setShipped] = useState({});
  const [allOrders, setAllOrders] = useState([]);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState("All");
  const [availableTypes, setAvailableTypes] = useState([]);
  const [clickedLinks, setClickedLinks] = useState(
    new Set(JSON.parse(localStorage.getItem("clickedLinks")) || [])
  );

  useEffect(() => {
    fetchOrders(selectedDate);
    fetchSearchableOrders(selectedDate);
  }, [selectedDate, sortOrder, sortColumn]);

  useEffect(() => {
    handleSearch(searchTerm);
  }, [orders, searchTerm, selectedTypeFilter, shipped]);

  const fetchOrders = async (date) => {
    setLoading(true);
    const apiUrl = `${settings.jiffyAPI}/get_ca_orders.php?date=${date}`;
    try {
      const response = await axios.get(apiUrl, { headers: authHeader() });

      if (response.data.success) {
        const initialNotes = {};
        const initialShipped = {};

        response.data.orders.forEach((order) => {
          initialNotes[order.po_number] = order.notes || "";
          initialShipped[order.po_number] =
            order.shipped === "1" || order.shipped === 1;
        });

        setNotes(initialNotes);
        setShipped(initialShipped);

        const sortedOrders = [...response.data.orders].sort((a, b) => {
          let aValue = a[sortColumn];
          let bValue = b[sortColumn];

          if (sortColumn === "commitment_date") {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
          } else if (sortColumn === "total_length") {
            aValue = parseFloat(aValue);
            bValue = parseFloat(bValue);
          } else if (sortColumn === "printer") {
            const getValue = (val) =>
              val === "Cancelled" ? 2 : val !== "Not Printed" ? 1 : 0;
            aValue = getValue(a.printer);
            bValue = getValue(b.printer);
          } else if (sortColumn === "email_date") {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
          } else if (sortColumn === "shipped") {
            aValue = shipped[a.po_number] ? 1 : 0;
            bValue = shipped[b.po_number] ? 1 : 0;
          } else {
            aValue = aValue?.toString().toLowerCase();
            bValue = bValue?.toString().toLowerCase();
          }

          if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
          if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
          return 0;
        });

        const ordersWithColors = sortedOrders.map((order) => {
          const isPrinted =
            order.printer &&
            order.printer !== "Not Printed" &&
            order.printer !== "Cancelled";
          const type = (order.order_type || "").toLowerCase();

          let backgroundColor = getOrderRowBackgroundColor(type, isPrinted);

          return {
            ...order,
            rowStyle: { backgroundColor },
          };
        });

        const types = [
          ...new Set(
            ordersWithColors.map((order) => order.order_type).filter(Boolean)
          ),
        ];
        setAvailableTypes(types);
        setOrders(ordersWithColors);
      } else {
        setOrders([]);
      }
    } catch (error) {
      toast("Failed to fetch orders", "error");
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };

  const handleSearch = (term) => {
    const lower = term.toLowerCase();
    let filtered = term ? allOrders : orders;

    if (term) {
      filtered = filtered.filter(
        (order) =>
          order.po_number.toLowerCase().includes(lower) ||
          order.name.toLowerCase().includes(lower) ||
          (notes[order.po_number] || "").toLowerCase().includes(lower)
      );
    }

    if (selectedTypeFilter !== "All") {
      filtered = filtered.filter((order) => {
        if (selectedTypeFilter === "Local Carrier") {
          return isLocalCarrierOrder(order);
        }
        return order.order_type === selectedTypeFilter;
      });
    }

    const coloredFiltered = filtered.map((order) => {
      const isPrinted =
        order.printer &&
        order.printer !== "Not Printed" &&
        order.printer !== "Cancelled";
      const type = order.order_type?.toLowerCase() || "";

      let backgroundColor = getOrderRowBackgroundColorSoft(type, isPrinted);

      return { ...order, rowStyle: { backgroundColor } };
    });

    setFilteredOrders(coloredFiltered);
  };

  const totalLength = filteredOrders.reduce(
    (sum, order) => sum + parseFloat(order.total_length || 0),
    0
  );

  const syncEmails = async () => {
    setSyncing(true);
    try {
      await axios.get(`${settings.jiffyAPI}/fetchEmails_ca.php`, {
        timeout: 240000,
        headers: authHeader(),
      });
      toast("Emails synced successfully!", "success");
      await fetchOrders(selectedDate);
    } catch (error) {
      toast("Failed to sync emails", "error");
    } finally {
      setSyncing(false);
    }
  };

  const handlePrinterSelection = async (order, selectedPrinter) => {
    try {
      await axios.post(
        `${settings.jiffyAPI}/update_printer_ca.php`,
        {
          po_number: order.po_number,
          printer: selectedPrinter,
          notes: notes[order.po_number],
        },
        { headers: authHeader() }
      );

      toast(`Printed on ${selectedPrinter}`, "success");

      setOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.po_number === order.po_number
            ? {
                ...o,
                printer: selectedPrinter,
                storedPrinter: selectedPrinter,
                rowColor:
                  selectedPrinter === "Cancelled"
                    ? "table-secondary"
                    : selectedPrinter !== "Not Printed"
                    ? "table-success"
                    : "table-danger",
              }
            : o
        )
      );
    } catch (error) {
      toast("Failed to update print status", "error");
    }
  };

  const handleNoteChange = (poNumber, newNote) => {
    setNotes({ ...notes, [poNumber]: newNote });
  };

  const handleNoteBlur = async (order) => {
    if (order.notes === notes[order.po_number]) return;

    try {
      await axios.post(
        `${settings.jiffyAPI}/update_printer_ca.php`,
        {
          po_number: order.po_number,
          printer: order.printer,
          notes: notes[order.po_number],
          shipped: shipped[order.po_number] ? 1 : 0,
        },
        { headers: authHeader() }
      );
      toast("Note updated successfully", "success");
    } catch (error) {
      toast("Failed to update note", "error");
    }
  };

  const handleLinkClick = (fileUrl) => {
    setClickedLinks((prev) => {
      const newLinks = new Set(prev);
      newLinks.add(fileUrl);
      localStorage.setItem("clickedLinks", JSON.stringify([...newLinks]));
      return newLinks;
    });
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const handleShippedChange = async (poNumber, isChecked, order) => {
    setShipped({ ...shipped, [poNumber]: isChecked });

    try {
      await axios.post(
        `${settings.jiffyAPI}/update_printer_ca.php`,
        {
          po_number: poNumber,
          printer: order.printer || "Not Printed",
          notes: notes[poNumber],
          shipped: isChecked ? 1 : 0,
        },
        { headers: authHeader() }
      );
      toast("Shipped status updated", "success");

      setOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.po_number === poNumber ? { ...o, shipped: isChecked ? 1 : 0 } : o
        )
      );
    } catch (error) {
      toast("Failed to update shipped status", "error");
    }
  };

  const renderSortArrow = (column) => {
    if (sortColumn !== column) return null;
    return sortOrder === "asc" ? " ↑" : " ↓";
  };

  const fetchSearchableOrders = async (date) => {
    const startDate = new Date(date);
    startDate.setDate(startDate.getDate() - 10);
    const endDate = new Date(date);
    endDate.setDate(endDate.getDate() + 5);

    const start = startDate.toISOString().split("T")[0];
    const end = endDate.toISOString().split("T")[0];

    try {
      const response = await axios.get(
        `${settings.jiffyAPI}/get_ca_orders.php?start=${start}&end=${end}`,
        { headers: authHeader() }
      );

      if (response.data.success) {
        const updatedShipped = {};
        response.data.orders.forEach((order) => {
          updatedShipped[order.po_number] =
            order.shipped === "1" || order.shipped === 1;
        });

        setShipped((prev) => ({ ...prev, ...updatedShipped }));
        setAllOrders(response.data.orders);
      }
    } catch (err) {
      toast("Search orders fetch failed", "error");
    }
  };

  const handleAttachmentClick = (attachment, order) => {
    // PDF için yeni sekmede aç
    if (attachment.type === "pdf") {
      window.open(`${settings.jiffyAPI}/${attachment.link}`, "_blank");
    }
    // PNG için modal veya yeni sekmede aç
    else if (
      attachment.type === "png" ||
      attachment.type === "jpg" ||
      attachment.type === "jpeg"
    ) {
      window.open(`${settings.jiffyAPI}/${attachment.link}`, "_blank");
    }
    // Diğer dosyalar için indir
    else {
      const link = document.createElement("a");
      link.href = `${settings.jiffyAPI}/${attachment.link}`;
      link.download = attachment.label || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    handleLinkClick(`${settings.jiffyAPI}/${attachment.link}`);
  };

  return (
    <div className="container-fluid mt-4 px-2">
      {syncing && (
        <div className="overlay">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Fetching orders...</span>
          </div>
          <p>Fetching new emails and updating order list...</p>
        </div>
      )}
      <div className="text-center mb-4">
        <h2>CA Orders</h2>
        <button
          className="btn btn-outline-danger mt-2"
          onClick={() => {
            if (window.confirm("Are you sure you want to logout?")) {
              localStorage.removeItem("token");
              localStorage.removeItem("email");
              window.location.href = "/";
            }
          }}
        >
          🚪 Logout
        </button>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <input
          type="date"
          className="form-control w-auto"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
        <select
          className="form-select w-auto mx-2"
          value={selectedTypeFilter}
          onChange={(e) => setSelectedTypeFilter(e.target.value)}
        >
          <option value="All">All Types</option>
          {availableTypes.includes("Local Carrier") ? null : (
            <option value="Local Carrier">Local Carrier</option>
          )}
          {availableTypes.map((type, i) => (
            <option key={i} value={type}>
              {type}
            </option>
          ))}
        </select>

        <input
          type="text"
          className="form-control w-auto mx-2"
          placeholder="Search by PO, Name, or Note"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <div className="d-flex align-items-center">
          <span className="me-3">
            <strong>Total:</strong> {filteredOrders.length}
          </span>
          <span className="me-3 text-success">
            <strong>Printed:</strong>{" "}
            {
              filteredOrders.filter(
                (order) =>
                  order.printer !== "Not Printed" &&
                  order.printer !== "Cancelled"
              ).length
            }
          </span>
          <span className="text-danger">
            <strong>Not Printed:</strong>{" "}
            {
              filteredOrders.filter((order) => order.printer === "Not Printed")
                .length
            }
          </span>
          <span className="ms-3 text-primary">
            <strong>Total Length:</strong> {Math.round(totalLength)} in
          </span>
          <span className="ms-3 text-info">
            <strong>Shipped:</strong>{" "}
            {filteredOrders.filter((order) => shipped[order.po_number]).length}
          </span>
        </div>

        <button
          className="btn btn-primary"
          onClick={syncEmails}
          disabled={syncing}
        >
          {syncing ? "Syncing..." : "Refresh Emails"}
        </button>
      </div>

      {loading ? (
        <p>Loading orders...</p>
      ) : (
        <div className="table-responsive" style={{ overflowX: "auto" }}>
          <table className="table table-bordered w-100">
            <thead>
              <tr>
                <th
                  style={{ minWidth: "120px", cursor: "pointer" }}
                  onClick={() => handleSort("po_number")}
                >
                  PO Number{renderSortArrow("po_number")}
                </th>
                <th
                  style={{ minWidth: "150px", cursor: "pointer" }}
                  onClick={() => handleSort("order_type")}
                >
                  Order Type{renderSortArrow("order_type")}
                </th>
                <th
                  style={{ minWidth: "100px", cursor: "pointer" }}
                  onClick={() => handleSort("name")}
                >
                  Customer Name{renderSortArrow("name")}
                </th>
                <th
                  style={{ minWidth: "100px", cursor: "pointer" }}
                  onClick={() => handleSort("total_length")}
                >
                  Total Length{renderSortArrow("total_length")}
                </th>
                <th
                  style={{ minWidth: "80px", cursor: "pointer" }}
                  onClick={() => handleSort("commitment_date")}
                >
                  Committed Date{renderSortArrow("commitment_date")}
                </th>
                <th
                  style={{ minWidth: "100px", cursor: "pointer" }}
                  onClick={() => handleSort("email_date")}
                >
                  Mail Received{renderSortArrow("email_date")}
                </th>

                <th style={{ minWidth: "120px" }}>Attachments</th>
                <th style={{ minWidth: "170px" }}>DTF Files</th>
                <th
                  style={{ minWidth: "100px", cursor: "pointer" }}
                  onClick={() => handleSort("printer")}
                >
                  Is Printed?{renderSortArrow("printer")}
                </th>
                <th
                  style={{ minWidth: "50px", cursor: "pointer" }}
                  onClick={() => handleSort("shipped")}
                >
                  Shipped?{renderSortArrow("shipped")}
                </th>

                <th style={{ minWidth: "150px" }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order, index) => (
                <tr key={index}>
                  <td style={order.rowStyle}>{order.po_number}</td>
                  <td>
                    {isLocalCarrierOrder(order)
                      ? "Local Carrier"
                      : order.order_type}
                  </td>

                  <td style={order.rowStyle}>{order.name}</td>
                  <td style={order.rowStyle}>{order.total_length} inches</td>
                  <td style={order.rowStyle}>{order.commitment_date}</td>
                  <td style={order.rowStyle}>
                    {order.email_date}
                    <br />
                    {order.email_time?.slice(0, 5)}
                  </td>

                  <td style={order.rowStyle}>
                    {order.attachments && order.attachments.length > 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {order.attachments.map((attachment, i) => (
                          <button
                            key={i}
                            className="btn btn-sm btn-outline-primary"
                            style={{
                              fontSize: "11px",
                              padding: "2px 6px",
                              marginBottom: "2px",
                              color: clickedLinks.has(
                                `${settings.jiffyAPI}/${attachment.link}`
                              )
                                ? "gray"
                                : "#0d6efd",
                            }}
                            onClick={() =>
                              handleAttachmentClick(attachment, order)
                            }
                            title={`${
                              attachment.label
                            } (${attachment.type.toUpperCase()})`}
                          >
                            {attachment.label} ({attachment.type.toUpperCase()})
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">No attachments</span>
                    )}
                  </td>

                  <td style={order.rowStyle}>
                    {order.gangsheets && order.gangsheets.length > 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {order.gangsheets.map((file, i) => (
                          <a
                            key={i}
                            href={file.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-success"
                            style={{
                              fontSize: "11px",
                              padding: "2px 6px",
                              marginBottom: "2px",
                              color: clickedLinks.has(file.link)
                                ? "gray"
                                : "#198754",
                            }}
                            onClick={() => handleLinkClick(file.link)}
                          >
                            {file.label} #{i + 1}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">No DTF files</span>
                    )}
                  </td>

                  <td style={{ ...order.rowStyle, minWidth: "170px" }}>
                    <select
                      className="form-select fw-bold"
                      value={order.printer || "Not Printed"}
                      onChange={(e) =>
                        handlePrinterSelection(order, e.target.value)
                      }
                    >
                      <option value="Not Printed">Not Printed</option>
                      <option value="Printer 1">Printer 1</option>
                      <option value="Printer 2">Printer 2</option>
                      <option value="Printer 3">Printer 3</option>
                      <option value="Printer 4">Printer 4</option>
                      <option value="Printer 5">Printer 5</option>
                      <option value="Printer 6">Printer 6</option>
                      <option value="UV Printer">UV Printer</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </td>

                  <td className="text-center" style={order.rowStyle}>
                    <input
                      type="checkbox"
                      style={{ width: "20px", height: "20px" }}
                      checked={shipped[order.po_number] || false}
                      onChange={(e) =>
                        handleShippedChange(
                          order.po_number,
                          e.target.checked,
                          order
                        )
                      }
                    />
                  </td>

                  <td style={order.rowStyle}>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{
                        fontSize: "12px",
                        height: "28px",
                        padding: "2px 6px",
                      }}
                      value={notes[order.po_number] || ""}
                      onChange={(e) =>
                        handleNoteChange(order.po_number, e.target.value)
                      }
                      onBlur={() => handleNoteBlur(order)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CAOrdersPage;
